import {
  EXAM_DURATION_MS,
  EXAM_TASKS,
  type ExamTaskKey,
  type ExamTest,
} from '@fluent/types';
import { uuid } from '../lib/uuid.js';

/**
 * One sitting of a mock exam — pure data + pure transitions, so the rules
 * (a bank word goes in ONE gap, the clock only runs while the test is open,
 * how points are counted) are unit-tested without React or storage.
 *
 * Answers are stored as letters, per task, keyed by 0-based item index:
 * `answers.vocabulary[3] = 'G'` means gap 4 holds bank word G.
 */

export type TaskAnswers = Partial<Record<number, string>>;
export type ExamAnswers = Record<ExamTaskKey, TaskAnswers>;

export interface ListeningState {
  /** What is left of the 40-second look-through before the first play. */
  previewLeftMs: number;
  /** Plays started (max 2). A play counts the moment it starts. */
  playsUsed: number;
}

export interface ExamAttempt {
  /** Identifies this sitting to the server: a finished task is recorded once
   *  per (attempt, task). A retake is a new attempt. */
  id: string;
  testId: string;
  startedAt: number;
  /** Time spent with the test open. Closing the app stops it (Tornike). */
  elapsedMs: number;
  answers: ExamAnswers;
  listening: ListeningState;
  /** The task the learner was on, so a resume lands where they left. */
  taskIndex: number;
  /** Tasks (1–6) already sent to the server as finished. */
  reported: number[];
}

export interface ExamResult {
  /** The sitting this came from — the server's key for it (exam_results).
   *  Missing only on results finished before 2026-09-26 (preview devices). */
  attemptId?: string;
  testId: string;
  score: number;
  byTask: Record<ExamTaskKey, number>;
  answers: ExamAnswers;
  elapsedMs: number;
  finishedAt: number;
  /** True when the clock ran out rather than the learner finishing. */
  timedOut: boolean;
}

export const LISTENING_PREVIEW_MS = 40_000;
export const LISTENING_MAX_PLAYS = 2;

/** Tasks whose options are a shared pool: each option fills ONE gap. */
const SHARED_POOL: ReadonlySet<ExamTaskKey> = new Set(['vocabulary', 'dialogue']);

function emptyAnswers(): ExamAnswers {
  return { listening: {}, matching: {}, reading: {}, vocabulary: {}, grammar: {}, dialogue: {} };
}

export function newAttempt(testId: string, now: number, id: string = uuid()): ExamAttempt {
  return {
    id,
    testId,
    startedAt: now,
    elapsedMs: 0,
    answers: emptyAnswers(),
    listening: { previewLeftMs: LISTENING_PREVIEW_MS, playsUsed: 0 },
    taskIndex: 0,
    reported: [],
  };
}

/** A sitting saved before attempts had an id (2026-09-26) gets one now.
 *  `makeId` runs only when an id is missing (it is read on every render). */
export function normalizeAttempt(attempt: ExamAttempt, makeId: () => string = uuid): ExamAttempt {
  if (attempt.id && Array.isArray(attempt.reported)) return attempt;
  return {
    ...attempt,
    id: attempt.id || makeId(),
    reported: Array.isArray(attempt.reported) ? attempt.reported : [],
  };
}

/**
 * Tasks that are now finished — every item answered — and not yet sent,
 * with how many of their answers are right. A task is the unit the missions
 * count ("finish 1 test task today"); each is sent once per attempt.
 */
export function newlyFinishedTasks(test: ExamTest, attempt: ExamAttempt): { task: number; correct: number }[] {
  const { byTask } = scoreAnswers(test, attempt.answers);
  const out: { task: number; correct: number }[] = [];
  EXAM_TASKS.forEach(({ key, items }, i) => {
    const task = i + 1;
    if (attempt.reported.includes(task)) return;
    if (answeredCount(attempt.answers, key) < items) return;
    out.push({ task, correct: byTask[key] });
  });
  return out;
}

export function markReported(attempt: ExamAttempt, tasks: number[]): ExamAttempt {
  if (tasks.length === 0) return attempt;
  return { ...attempt, reported: [...new Set([...attempt.reported, ...tasks])].sort((a, b) => a - b) };
}

/**
 * Put `letter` in item `index` of `task`. In the shared-pool tasks a word
 * or line can only sit in one gap, so choosing it here takes it out of the
 * gap that held it — the way a learner moves a card on paper.
 */
export function setAnswer(
  attempt: ExamAttempt,
  task: ExamTaskKey,
  index: number,
  letter: string,
): ExamAttempt {
  const current: TaskAnswers = { ...attempt.answers[task] };
  if (SHARED_POOL.has(task)) {
    for (const [key, value] of Object.entries(current)) {
      if (value === letter) delete current[Number(key)];
    }
  }
  current[index] = letter;
  return { ...attempt, answers: { ...attempt.answers, [task]: current } };
}

export function clearAnswer(attempt: ExamAttempt, task: ExamTaskKey, index: number): ExamAttempt {
  const current: TaskAnswers = { ...attempt.answers[task] };
  delete current[index];
  return { ...attempt, answers: { ...attempt.answers, [task]: current } };
}

export function itemCount(task: ExamTaskKey): number {
  return EXAM_TASKS.find((t) => t.key === task)?.items ?? 0;
}

export function answeredCount(answers: ExamAnswers, task: ExamTaskKey): number {
  return Object.values(answers[task]).filter(Boolean).length;
}

export function unansweredTotal(answers: ExamAnswers): number {
  return EXAM_TASKS.reduce((n, t) => n + (t.items - answeredCount(answers, t.key)), 0);
}

/** The keyed letter for one item. */
export function correctLetter(test: ExamTest, task: ExamTaskKey, index: number): string | undefined {
  switch (task) {
    case 'listening':
      return test.listening.questions[index]?.answer;
    case 'matching':
      return test.matching.questions[index]?.answer;
    case 'reading':
      return test.reading.questions[index]?.answer;
    case 'vocabulary':
      return test.vocabulary.answers[index];
    case 'grammar':
      return test.grammar.gaps[index]?.answer;
    case 'dialogue':
      return test.dialogue.answers[index];
  }
}

export function scoreAnswers(
  test: ExamTest,
  answers: ExamAnswers,
): { score: number; byTask: Record<ExamTaskKey, number> } {
  const byTask = {} as Record<ExamTaskKey, number>;
  let score = 0;
  for (const { key, items } of EXAM_TASKS) {
    let right = 0;
    for (let i = 0; i < items; i++) {
      if (answers[key][i] !== undefined && answers[key][i] === correctLetter(test, key, i)) right++;
    }
    byTask[key] = right;
    score += right;
  }
  return { score, byTask };
}

export function finishAttempt(
  test: ExamTest,
  attempt: ExamAttempt,
  now: number,
  timedOut: boolean,
): ExamResult {
  const { score, byTask } = scoreAnswers(test, attempt.answers);
  return {
    attemptId: attempt.id,
    testId: attempt.testId,
    score,
    byTask,
    answers: attempt.answers,
    elapsedMs: Math.min(attempt.elapsedMs, EXAM_DURATION_MS),
    finishedAt: now,
    timedOut,
  };
}

export function remainingMs(elapsedMs: number): number {
  return Math.max(0, EXAM_DURATION_MS - elapsedMs);
}

/** 1:59:07 — hours always shown, like an exam-room clock. */
export function formatClock(ms: number): string {
  const total = Math.ceil(Math.max(0, ms) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Keep the better of two results (higher score; on a tie, the newer). */
export function betterResult(a: ExamResult | undefined, b: ExamResult): ExamResult {
  if (!a) return b;
  return b.score >= a.score ? b : a;
}
